import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CheckValid from "../../Common/CheckValid";
import { WARRANTY_SUBSCRIPTION_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasOneOfWarrantyPermissions } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import { listPlanTiers, listSubscriptions, searchSubscriptions } from "../common/warrantyApi";
import { usePagedList } from "../common/usePagedList";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate, formatMinor } from "../common/format";
import {
  attentionLabel,
  billingModeLabel,
  salesChannelLabel,
  subscriptionStatusLabel,
} from "../common/opsLabels";
import { saveFile } from "../common/saveFile";
import {
  COVERED,
  COVERED_STATUSES,
  CSV_ROW_CAP,
  PAGE_SIZE,
  SALES_CHANNEL_FILTERS,
  isCovered,
  NOT_BOUGHT_FILTERS,
  neverPaid,
  STATUS_FILTERS,
  collectSubscriptions,
  filtersFromParams,
  filtersToParams,
  listQuery,
  subscriptionsCsv,
  subscriptionsCsvName,
  tierKeyMessage,
} from "../common/subscriptionList";
import "../warranty.css";

const KINDS = {
  frameNumber: {
    label: "Frame number",
    check: "warranty_frame",
    placeholder: "for example ZZ.EXAMPLE.0001",
    max: 40,
  },
  phone: { label: "Phone", check: "phone", placeholder: "10 digit mobile number", max: 20 },
  subscriptionId: {
    label: "Plan id",
    check: "warranty_uuid",
    placeholder: "0199a1b2-0000-7000-8000-000000000001",
    max: 36,
  },
};

const STATUS_CHIP_LABEL = {
  covered: "Covered now",
  active: "Active",
  completed: "Paid in full",
  cancelling: "Ending soon",
  paused: "Payment missed",
  cancelled: "Cancelled",
  refunded: "Refunded",
  all: "All",
  pending: "Checkout not paid",
  ended: "Ended",
};

const detailUrl = (item) => `${WARRANTY_SUBSCRIPTION_URL}/${encodeURIComponent(item.subscriptionId)}`;
const dash = (value) => (value === null || value === undefined || value === "" ? "-" : value);
const plural = (n, one, many) => `${n.toLocaleString("en-IN")} ${n === 1 ? one : many}`;

const statusCount = (counts, status) => {
  if (!counts) {
    return null;
  }
  if (status === COVERED) {
    const parts = COVERED_STATUSES.map((s) => counts.byStatus?.[s]);
    return parts.every((n) => typeof n === "number") ? parts.reduce((a, n) => a + n, 0) : null;
  }
  const value = status === "all" ? counts.all : counts.byStatus?.[status];
  return typeof value === "number" ? value : null;
};

const ChipCount = ({ value }) =>
  value === null || value === undefined ? null : <span className="warranty-tab-count">{value}</span>;

const StatusBadges = ({ item }) => (
  <>
    <span className={`warranty-badge is-sub-${item.status}`}>
      {neverPaid(item) ? "Never paid" : item.status === "ended" ? "Cover ended" : subscriptionStatusLabel(item.status)}
    </span>
    {(Array.isArray(item.attention) ? item.attention : [])
      // why: the status badge already says Paused; the same word twice adds nothing.
      .filter((reason) => !(reason === "paused" && item.status === "paused"))
      .map((reason) => (
        <span key={reason} className="warranty-badge is-warning warranty-attention-badge">
          {attentionLabel(reason)}
        </span>
      ))}
  </>
);

const CouponIn = ({ item }) => {
  if (typeof item.couponMonthsToGo !== "number") {
    return "-";
  }
  return (
    <span title={item.couponExpectedOn ? `Expected on ${formatDate(item.couponExpectedOn)}` : undefined}>
      {plural(item.couponMonthsToGo, "month", "months")}
    </span>
  );
};

// The filtered list (the default view). Filters are non-personal and live in the URL so Back from
// a subscription returns to the same list; the search value below never does.
const SubscriptionList = ({ accessToken, enabled }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const paramText = params.toString();
  const filters = useMemo(() => filtersFromParams(new URLSearchParams(paramText)), [paramText]);
  const { status, needsAttention, tier, billingMode, salesChannel, dueWithinDays, sort } = filters;

  const setFilter = (patch) => setParams(filtersToParams({ ...filters, ...patch }), { replace: true });

  // Counts come on the first page only and depend on tier, billing mode and sales channel alone, so
  // they stay on the chips while another status loads, and go when one of those changes. Each answer
  // carries the key of the request that fetched it, so old counts are never filed under new filters.
  // why: whether the service narrows its counts by sales channel is its choice; keying on it costs
  // only a moment without numbers, and never shows the app's counts under "Support bot".
  const countsKey = `${tier}|${billingMode}|${salesChannel}`;
  const fetchPage = useCallback(
    async (cursor) => {
      let result = await listSubscriptions(
        accessToken,
        listQuery({ status, needsAttention, tier, billingMode, salesChannel, dueWithinDays, sort }, cursor, PAGE_SIZE)
      );
      if (result.ok && result.data && status === COVERED) {
        // why: the service filters one status at a time, so "Covered now" reads every status and
        // keeps the covered rows; a page may hold fewer than PAGE_SIZE, and Load more reads on.
        result = { ...result, data: { ...result.data, items: (result.data.items || []).filter(isCovered) } };
      }
      return result.ok && result.data
        ? { ...result, data: { ...result.data, countsKey: `${tier}|${billingMode}|${salesChannel}` } }
        : result;
    },
    [accessToken, status, needsAttention, tier, billingMode, salesChannel, dueWithinDays, sort]
  );
  const list = usePagedList(fetchPage, enabled);

  const lastCounts = useRef({ key: null, counts: null });
  if (list.firstPage?.counts && typeof list.firstPage.countsKey === "string") {
    lastCounts.current = { key: list.firstPage.countsKey, counts: list.firstPage.counts };
  }
  const counts = lastCounts.current.key === countsKey ? lastCounts.current.counts : null;

  // why: a picker of the market's tiers when the plans list can be read; staff with ops rights only
  // (no plans rights) are refused there, and keep the typed key as before.
  const [tierOptions, setTierOptions] = useState(null);
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let live = true;
    listPlanTiers(accessToken).then((result) => {
      const tiers = result.ok && Array.isArray(result.data?.tiers) ? result.data.tiers : [];
      if (live && tiers.length > 0) {
        setTierOptions(tiers.map((t) => ({ key: t.tier, label: t.displayName || t.tier })));
      }
    });
    return () => {
      live = false;
    };
  }, [accessToken, enabled]);

  const [tierDraft, setTierDraft] = useState(tier);
  const [tierError, setTierError] = useState("");
  useEffect(() => {
    setTierDraft(tier);
    setTierError("");
  }, [tier]);

  const applyTier = () => {
    const text = tierDraft.trim();
    if (text === tier) {
      setTierError("");
      return;
    }
    const message = text === "" ? "" : tierKeyMessage(text);
    setTierError(message);
    if (message === "") {
      setFilter({ tier: text });
    }
  };

  const [csv, setCsv] = useState({ inFlight: false, note: "", message: "" });
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const downloadCsv = async () => {
    if (csv.inFlight) {
      return;
    }
    // The file is of the filters as they were on the click, and is named after them.
    const snapshot = filters;
    setCsv({ inFlight: true, note: "", message: "" });
    const result = await collectSubscriptions((query) => listSubscriptions(accessToken, query), snapshot);
    if (!mounted.current) {
      return;
    }
    if (!result.ok) {
      setCsv({ inFlight: false, note: "", message: result.message });
      return;
    }
    if (result.rows.length === 0) {
      setCsv({ inFlight: false, note: "Nothing to save: no customers match these filters.", message: "" });
      return;
    }
    const filename = subscriptionsCsvName(snapshot.status);
    saveFile(new Blob([subscriptionsCsv(result.rows)], { type: "text/csv;charset=utf-8" }), filename);
    const note = result.capped
      ? `Saved the first ${CSV_ROW_CAP.toLocaleString("en-IN")} subscriptions only to ${filename}. Narrow the filters to get the rest.`
      : `Saved ${plural(result.rows.length, "subscription", "subscriptions")} to ${filename}.`;
    setCsv({ inFlight: false, note, message: "" });
  };

  const open = (item) => navigate(detailUrl(item));

  return (
    <section aria-label="Subscription list">
      <div className="warranty-window-picker warranty-status-chips" role="group" aria-label="Status">
        {STATUS_FILTERS.filter((key) => !NOT_BOUGHT_FILTERS.includes(key)).map((key) => (
          <button
            key={key}
            type="button"
            className={`warranty-inline-btn${status === key ? " is-selected" : ""}`}
            aria-pressed={status === key}
            onClick={() => status !== key && setFilter({ status: key })}
          >
            {STATUS_CHIP_LABEL[key]} <ChipCount value={statusCount(counts, key)} />
          </button>
        ))}
        <button
          type="button"
          className={`warranty-inline-btn${needsAttention ? " is-selected" : ""}`}
          aria-pressed={needsAttention}
          // why: the count covers every status, so turning the chip on lists every status too; a
          // status picked afterwards keeps the chip on.
          onClick={() =>
            setFilter(needsAttention ? { needsAttention: false } : { needsAttention: true, status: "all" })
          }
        >
          Needs attention <ChipCount value={typeof counts?.needsAttention === "number" ? counts.needsAttention : null} />
        </button>
      </div>
      <div className="warranty-window-picker warranty-status-chips" role="group" aria-label="Did not buy">
        <span className="warranty-muted">Did not buy:</span>
        {NOT_BOUGHT_FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={`warranty-inline-btn${status === key ? " is-selected" : ""}`}
            aria-pressed={status === key}
            onClick={() => status !== key && setFilter({ status: key })}
          >
            {STATUS_CHIP_LABEL[key]} <ChipCount value={statusCount(counts, key)} />
          </button>
        ))}
      </div>

      <div className="formloginset warranty-list-filters">
        <Form.Group controlId="warrantyListTier">
          <Form.Label>Tier</Form.Label>
          {tierOptions ? (
            <Form.Select value={tier} onChange={(event) => setFilter({ tier: event.target.value })}>
              <option value="">Any</option>
              {tierOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          ) : (
            <Form.Control
              type="text"
              value={tierDraft}
              maxLength={32}
              placeholder="for example assure_plus"
              onChange={(event) => setTierDraft(event.target.value)}
              onBlur={applyTier}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  applyTier();
                }
              }}
              isInvalid={tierError !== ""}
              aria-describedby="warrantyListTierMsg"
              autoComplete="off"
              spellCheck={false}
            />
          )}
        </Form.Group>
        <Form.Group controlId="warrantyListBilling">
          <Form.Label>Billing</Form.Label>
          <Form.Select value={billingMode} onChange={(event) => setFilter({ billingMode: event.target.value })}>
            <option value="">Any</option>
            <option value="monthly">Monthly</option>
            <option value="one_time">One time</option>
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="warrantyListSoldVia">
          <Form.Label>Sold via</Form.Label>
          <Form.Select value={salesChannel} onChange={(event) => setFilter({ salesChannel: event.target.value })}>
            <option value="">Any</option>
            {SALES_CHANNEL_FILTERS.map((key) => (
              <option key={key} value={key}>
                {salesChannelLabel(key)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="warrantyListDue">
          <Form.Label>Next due within</Form.Label>
          <Form.Select value={dueWithinDays} onChange={(event) => setFilter({ dueWithinDays: event.target.value })}>
            <option value="">Any</option>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">30 days</option>
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="warrantyListSort">
          <Form.Label>Sort</Form.Label>
          <Form.Select value={sort} onChange={(event) => setFilter({ sort: event.target.value })}>
            <option value="next_due_asc">Next due first</option>
            <option value="started_desc">Newest start first</option>
          </Form.Select>
        </Form.Group>
        <div className="warranty-list-csv">
          <Button
            className="Addbtn"
            onClick={downloadCsv}
            disabled={csv.inFlight}
            aria-describedby="warrantyListCsvCap"
          >
            {csv.inFlight ? "Preparing CSV" : "Download CSV"}
          </Button>
          <small className="warranty-muted" id="warrantyListCsvCap">
            Capped at {CSV_ROW_CAP.toLocaleString("en-IN")} rows
          </small>
        </div>
      </div>
      <div className="warranty-lookup-msg" id="warrantyListTierMsg" aria-live="polite">
        {tierError && (
          <span className="danger-colorset" role="alert">
            {tierError}
          </span>
        )}
        {csv.message && (
          <span className="danger-colorset" role="alert">
            {csv.message}
          </span>
        )}
        {csv.note && <span className="warranty-muted">{csv.note}</span>}
      </div>

      {list.status === "loading" && <Loading label="Loading subscriptions" />}
      {list.status === "error" && <ErrorState message={list.message} onRetry={() => list.reload()} />}
      {list.status === "ready" && list.items.length === 0 && (
        <EmptyState title="No customers match these filters">
          <span>Try another status or clear a filter.</span>
        </EmptyState>
      )}
      {list.status === "ready" && list.items.length > 0 && (
        <>
          <div className="tableView warranty-ops-table">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Frame number</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Plan</th>
                  <th scope="col">Billing</th>
                  <th scope="col">Sold via</th>
                  <th scope="col">Status</th>
                  <th scope="col">Paid</th>
                  <th scope="col" className="warranty-num">
                    Instalments left
                  </th>
                  <th scope="col" className="warranty-num">
                    Amount left
                  </th>
                  <th scope="col">Next due</th>
                  <th scope="col">Grace ends</th>
                  <th scope="col">Started</th>
                  <th scope="col">Coupon in</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item) => (
                  <tr
                    key={item.subscriptionId}
                    className="warranty-row-link"
                    onClick={(event) => {
                      // A mouse shortcut only: keyboard users have the frame number link, whose
                      // own click already navigates.
                      if (event.target.closest("a, button")) {
                        return;
                      }
                      open(item);
                    }}
                  >
                    <td className="warranty-nowrap">
                      <Link to={detailUrl(item)}>{dash(item.frameNumber)}</Link>
                    </td>
                    <td className="warranty-nowrap">{dash(item.maskedPhone)}</td>
                    <td className="warranty-nowrap">{dash(item.displayName || item.tier)}</td>
                    <td className="warranty-nowrap">{billingModeLabel(item.billingMode)}</td>
                    <td className="warranty-nowrap">{salesChannelLabel(item.salesChannel)}</td>
                    <td>
                      <StatusBadges item={item} />
                    </td>
                    <td className="warranty-nowrap">
                      {typeof item.monthsPaid === "number" && typeof item.tenureMonths === "number"
                        ? `${item.monthsPaid} / ${item.tenureMonths}`
                        : "-"}
                    </td>
                    <td className="warranty-num">{dash(item.instalmentsLeft)}</td>
                    <td className="warranty-num warranty-nowrap">
                      {formatMinor(item.amountLeftMinor, item.currency || "INR")}
                    </td>
                    <td className="warranty-nowrap">{formatDate(item.nextDueOn)}</td>
                    <td className="warranty-nowrap">{formatDate(item.graceEndsOn)}</td>
                    <td className="warranty-nowrap">{formatDate(item.startedAt)}</td>
                    <td className="warranty-nowrap">
                      <CouponIn item={item} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="warranty-more">
            {list.moreError && (
              <span className="warranty-field-msg is-error" role="alert">
                {list.moreError}
              </span>
            )}
            {list.nextCursor && (
              <Button className="Addbtn" onClick={list.loadMore} disabled={list.loadingMore}>
                {list.loadingMore ? "Loading" : "Load more"}
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
};

// The typed search value is held in component state only: it never goes into the URL, the route or
// the browser history, and it is posted in the body of the search.
const SubscriptionLookup = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasOneOfWarrantyPermissions(selfInfo, [WARRANTY_PERMISSION.OPS, WARRANTY_PERMISSION.VIEW]);
  useWarrantyNav(allowed);

  const [kind, setKind] = useState("frameNumber");
  const [value, setValue] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [result, setResult] = useState({ status: "idle", items: [], message: "" });
  const seq = useRef(0);

  const validate = (text, which = kind) => {
    let message = "";
    CheckValid(text.trim(), {
      type: KINDS[which].check,
      error: (m) => {
        message = m;
      },
    });
    setFieldError(message);
    return message === "";
  };

  const search = async () => {
    if (result.status === "loading") {
      return;
    }
    if (!validate(value)) {
      return;
    }
    const mine = ++seq.current;
    setResult({ status: "loading", items: [], message: "" });
    const response = await searchSubscriptions(accessToken, { [kind]: value.trim() });
    if (mine !== seq.current) {
      return;
    }
    if (response.ok) {
      setResult({ status: "ready", items: response.data?.items || [], message: "" });
    } else {
      setResult({ status: "error", items: [], message: response.message });
    }
  };

  const clearSearch = () => {
    seq.current += 1;
    setFieldError("");
    setResult({ status: "idle", items: [], message: "" });
  };

  const changeKind = (next) => {
    clearSearch();
    setKind(next);
    setValue("");
  };

  const searching = result.status !== "idle";

  return (
    <WarrantyPage
      title="Assure customers"
      sub="Everyone who bought Assure or Assure+, one-time or monthly. Covered now is who has plan cover today. Find one by frame number, phone or plan id."
      allowed={allowed}
      deniedTitle="You do not have access to subscriptions"
      deniedText="Ask an admin to give you the Warranty Operations permission."
    >
      <form
        className="formloginset warranty-lookup"
        onSubmit={(event) => {
          event.preventDefault();
          search();
        }}
        autoComplete="off"
      >
        <Form.Group controlId="warrantyLookupKind">
          <Form.Label>Search by</Form.Label>
          <Form.Select value={kind} onChange={(event) => changeKind(event.target.value)}>
            {Object.entries(KINDS).map(([key, item]) => (
              <option key={key} value={key}>
                {item.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="warrantyLookupValue" className="warranty-lookup-value">
          <Form.Label>{KINDS[kind].label}</Form.Label>
          <Form.Control
            type="text"
            value={value}
            maxLength={KINDS[kind].max}
            placeholder={KINDS[kind].placeholder}
            onChange={(event) => {
              setValue(event.target.value);
              if (fieldError) {
                validate(event.target.value);
              }
            }}
            autoComplete="off"
            spellCheck={false}
          />
        </Form.Group>
        <Button className="Addbtn warranty-lookup-btn" type="submit" disabled={result.status === "loading"}>
          {result.status === "loading" ? "Searching" : "Search"}
        </Button>
        {searching && (
          <button type="button" className="warranty-inline-btn warranty-lookup-btn" onClick={clearSearch}>
            Back to the list
          </button>
        )}
      </form>
      <div className="warranty-lookup-msg" aria-live="polite">
        {fieldError && (
          <span className="danger-colorset" role="alert">
            {fieldError}
          </span>
        )}
      </div>

      {!searching && <SubscriptionList accessToken={accessToken} enabled={allowed} />}

      {result.status === "loading" && <Loading label="Searching" />}
      {result.status === "error" && <ErrorState message={result.message} onRetry={search} />}
      {result.status === "ready" && result.items.length === 0 && (
        <EmptyState title="No subscription matches">
          <span>Check the value and try again.</span>
        </EmptyState>
      )}
      {result.status === "ready" && result.items.length > 0 && (
        <div className="tableView warranty-ops-table">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Frame number</th>
                <th scope="col">Phone</th>
                <th scope="col">Product</th>
                <th scope="col">Plan</th>
                <th scope="col">Status</th>
                <th scope="col">Started</th>
                <th scope="col">
                  <span className="warranty-sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {result.items.map((item) => (
                <tr key={item.subscriptionId}>
                  <td>{item.frameNumber}</td>
                  <td>{item.maskedPhone || "-"}</td>
                  <td>{item.productName}</td>
                  <td>{item.displayName || item.tier}</td>
                  <td>
                    <span className={`warranty-badge is-sub-${item.status}`}>
                      {subscriptionStatusLabel(item.status)}
                    </span>
                  </td>
                  <td>{formatDate(item.startedAt)}</td>
                  <td className="warranty-actions-cell">
                    <Link
                      className="warranty-inline-btn is-primary"
                      to={detailUrl(item)}
                      aria-label={`Open the subscription of ${item.frameNumber}`}
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </WarrantyPage>
  );
};

export default SubscriptionLookup;
